import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import FocusLock from "react-focus-lock";
import { isUsersnapActive } from "@labxchange/util-usersnap";
import { FormattedMessage } from "@learningblocks/shared-components/lib/FormattedMessage";
import {
  sanitizeUnsafeHTML,
  sanitizeConfigOptions,
} from "@labxchange/util-sanitization";
import { decodeHtmlEntities } from "../utils/stringUtils";
import plusIcon from "@common/images/plus.svg";
// import { ReactComponent as LockIcon } from "@common/images/locked.svg";
import LockUrl from "@common/images/locked.svg";
import { Button } from "@labxchange/buttons";
import { CloseIcon } from "@learningblocks/shared-components/lib/Icons";
import { useIntl } from "react-intl";

import className from "../style.module.scss";

export function IntroModal(props) {
  const [imageUrl, setImageUrl] = useState(null);
  const intl = useIntl();

  const baseUrl =
    window.CONTENT_BASE_URL ||
    window.location.origin + window.location.pathname;

  useEffect(() => {
    if (props.image) {
      loadImage(props.image, intl.locale)
        .then(({ imageUrl }) => {
          setImageUrl(imageUrl);
        })
        .catch((err) => {
          setImageUrl(null);
          console.error(err.message);
        });
    }
  }, [props.image, intl.locale]);

  const renderLegendItem = (icon, text) => {
    return (
      <div className={className.LegendItem}>
        {icon}
        <div>
          <FormattedMessage
            string={text}
            allowAdditionalTags={["b"]}
            description=""
          />
        </div>
      </div>
    );
  };

  const renderHotSpot = (type) => {
    let hotspotIcon = <img alt="" src={plusIcon} />;
    if (type === "locked") {
      hotspotIcon = (
        <img alt="locked" src={LockUrl} className={className.lockIcon} />
      );
    }

    return (
      <div
        className={`${className.hotspot} ${type === "locked" ? className.locked : ""}`}
      >
        {hotspotIcon}
      </div>
    );
  };

  const loadImage = (src, lng) => {
    return new Promise((resolve, reject) => {
      const tryLoadImage = (language) => {
        const image = new Image();
        const imageUrl = src.includes(window.location.origin)
          ? src
          : `${baseUrl}locales/${language}/images/${src}`;

        image.src = imageUrl;
        image.onload = () => resolve({ imageUrl });
        image.onerror = () => {
          if (language !== "en") {
            tryLoadImage("en");
          } else {
            reject(new Error(`Failed to load image from ${imageUrl}`));
          }
        };
      };

      tryLoadImage(lng);
    });
  };

  const buttonText = {
    id: "Start",
    defaultMessage: props.buttonText ? props.buttonText : "Start",
    description: props.buttonText ? props.buttonText : "Start",
  };

  return (
    <FocusLock disabled={isUsersnapActive() || props.isDisabledFocusLock}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={`IntroModalIntro_`}
        aria-describedby={`IntroModalIntro_`}
        className={`${className.IntroModalContainer}`}
      >
        <div className={className.IntroModalWrapper}>
          <div className={className.IntroModal}>
            <button
              className={`${className.closeBtn} ${className.button}`}
              tabIndex={0}
              onClick={() => {
                props.closeIntroModal();
              }}
              aria-label={intl.formatMessage({
                id: "Close",
                defaultMessage: "Close",
              })}
            >
              <FormattedMessage string={"Close"} description="" />
              <img alt="" src={CloseIcon} />
            </button>
            {props.image && (
              <img
                className={className.animationEntityContainer}
                alt={
                  props.altText
                    ? decodeHtmlEntities(
                        sanitizeUnsafeHTML(
                          intl.formatMessage({
                            id: props.altText,
                            defaultMessage: props.altText,
                          }),
                          sanitizeConfigOptions.PlainText
                        )
                      )
                    : ""
                }
                src={imageUrl}
              />
            )}
            <div id={`IntroModalIntro_`} className={className.IntroModalIntro}>
              <FormattedMessage
                string={props.text}
                sanitizeConfig={sanitizeConfigOptions.LightBlockText}
                description=""
              />
            </div>
            {!(
              props.hideHotspotInstruction && props.hideLockedHotspotInstruction
            ) && (
              <div className={className.Legend}>
                {!props.hideHotspotInstruction &&
                  renderLegendItem(
                    renderHotSpot("default"),
                    "<b>Hotspot:</b> Click on hotspots to learn more."
                  )}
                {!props.hideLockedHotspotInstruction &&
                  renderLegendItem(
                    renderHotSpot("locked"),
                    "<b>Locked hotspot:</b> Complete the active hotspots to open the locked ones."
                  )}
              </div>
            )}
            <Button
              className="primary-button"
              tabIndex={0}
              onClick={() => {
                props.closeIntroModal();
              }}
              titleDescriptor={buttonText}
              iconName={!props.isLastChapter ? "chevron-right" : null}
              textBeforeIcon={true}
            />
          </div>
        </div>
      </div>
    </FocusLock>
  );
}

IntroModal.propTypes = {
  closeIntroModal: PropTypes.func.isRequired,
};
